import { ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem, Button } from "reactstrap";
import CollapsablePanel from "../../common/collapsable-panel/collapsable-panel";
import { useState, useCallback, useEffect } from "react";
import { useMutation } from "@apollo/client";
import Spacer from "../../common/spacer";
import { AddIcon, DeleteIcon } from "../../common/icons";
import { warn } from "../../../services/env";
import useToggle from "../../../hook/useToggle";
import useCheckGroup from "../../../hook/useCheckGroup";
import { IPosition } from "../../../types/position";
import { DELETE_POSITION_ADDRESSES, DELETE_POSITION_CONTACT_PHONES, DELETE_POSITION_EMAILS, GET_POSITION_ADDRESSES, GET_POSITION_CONTACT_PHONES, GET_POSITION_EMAILS } from "../../../graphql/contact-gql";
import confirm from "../../common/confirm";
import { notifySuccess } from "../../../services/notification";
import SpinnerOverlay from "../../common/spinner/spinner";
import PositionAddress from "./position-address/position-address";
import PositionPhone from "./position-phone/position-phone";
import PositionEmail from "./position-email/position-email";
import AddressCreate from "./position-address/address-create";
import PhoneCreate from "./position-phone/phone-create";
import EmailCreate from "./position-email/email-create";
import useAuth from "../../../hook/useAuth";

type ContactInfoProps = {
    position: IPosition;
}
const PositionContactInfo = (props: ContactInfoProps) => {
    const {position} = props
    const { isFrenchTranslator } = useAuth()
    const [cloned,setCloned] = useState<boolean>(false);
    const [deletePositionAddresses, {loading: delAddressLoading}] = useMutation(DELETE_POSITION_ADDRESSES);
    const [deletePositionPhones, {loading: delPhoneLoading}] = useMutation(DELETE_POSITION_CONTACT_PHONES);
    const [deletePositionDigital, {loading: delDigitalLoading}] = useMutation(DELETE_POSITION_EMAILS);

    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const toggleDropdownOpen = useCallback(() => {
        setDropdownOpen(!dropdownOpen)
    }, [dropdownOpen])

    const {isOpen: addressAddOpen, toggle: toggleAddressAdd} = useToggle()
    const {isOpen: contactPhoneAddOpen, toggle: toggleContactPhoneAdd} = useToggle()
    const {isOpen: digitalAddressAddOpen, toggle: toggleDigitalAddressAdd} = useToggle()

    const {checkGroup: addressCheckGroup} = useCheckGroup<number>()
    const {checkGroup: phoneCheckGroup} = useCheckGroup<number>()
    const {checkGroup: digitalAddressCheckGroup} = useCheckGroup<number>()

    const isNoneSelected = (): boolean => {
        return addressCheckGroup.getCheckedValues()?.length === 0
            && phoneCheckGroup.getCheckedValues()?.length === 0
            && digitalAddressCheckGroup.getCheckedValues()?.length === 0
    }

    const isCrossSectionSelected = (): boolean => {
        return addressCheckGroup.getCheckedValues()?.length > 0 && (phoneCheckGroup.getCheckedValues()?.length > 0 || digitalAddressCheckGroup.getCheckedValues()?.length > 0)
            || phoneCheckGroup.getCheckedValues()?.length > 0 && digitalAddressCheckGroup.getCheckedValues()?.length > 0
    }

    const removeContacts = () => {
        if (isCrossSectionSelected()) {
            warn({
                title: "Warning!",
                content: `You can only select/remove entries from one module at a time<br/> (e.g. ${getPositionLbl()} Addresses Information or  ${getPositionLbl()} Phone Numbers or  ${getPositionLbl()} Email Addresses).`
            })
        } else {
            if (addressCheckGroup.getCheckedValues()?.length > 0) {
                removeAddress()
            } else if (phoneCheckGroup.getCheckedValues()?.length > 0) {
                removePhone()
            } else if (digitalAddressCheckGroup.getCheckedValues()?.length > 0) {
                removeDigitalAddress()
            }
        }
    }

    const removeAddress = () => {
        confirm({
            title: `Remove ${getPositionLbl()} Addresses?`,
            content: `The selected addresses will be removed. <br/>Are you sure you want to continue?`,
            okText: <><DeleteIcon/> Remove</>,
            okBtnColor: 'danger'
        }).then(() => {
            deletePositionAddresses({
                variables: {
                    positionId: position.positionId,
                    addressUsageIds: addressCheckGroup.getCheckedValues()
                },
                refetchQueries: [{
                    query: GET_POSITION_ADDRESSES,
                    variables: { positionId: position.positionId }
                }],
                awaitRefetchQueries: true,
                onCompleted: (data) => {
                    data && notifySuccess()
                }
            });
        })
    }

    const removePhone = () => {
        confirm({
            title: `Remove ${getPositionLbl()} Phone Numbers?`,
            content: `The selected phone numbers will be removed. <br/>Are you sure you want to continue?`,
            okText: <><DeleteIcon/> Remove</>,
            okBtnColor: 'danger'
        }).then(() => {
            deletePositionPhones({
                variables: {
                    positionId: position.positionId,
                    contactPhoneIds: phoneCheckGroup.getCheckedValues()
                },
                refetchQueries: [{
                    query: GET_POSITION_CONTACT_PHONES,
                    variables: { positionId: position.positionId }
                }],
                awaitRefetchQueries: true,
                onCompleted: (data) => {
                    data && notifySuccess()
                }
            });
        })
    }

    const removeDigitalAddress = () => {
        confirm({
            title: `Remove ${getPositionLbl()} Email Addresses?`,
            content: `The selected email addresses will be removed. <br/>Are you sure you want to continue?`,
            okText: <><DeleteIcon/> Remove</>,
            okBtnColor: 'danger'
        }).then(() => {
            deletePositionDigital({
                variables: {
                    positionId: position.positionId,
                    digitalAddressIds: digitalAddressCheckGroup.getCheckedValues()
                },
                refetchQueries: [{
                    query: GET_POSITION_EMAILS,
                    variables: { positionId: position.positionId }
                }],
                awaitRefetchQueries: true,
                onCompleted: (data) => {
                    data && notifySuccess()
                }
            });
        })
    }

    const getPositionLbl = () => position?.isRole ? 'Role' : 'Position'
    const getPositionLbl2 = () => position?.isRole ? 'role' : 'position'
    useEffect(()=>{
        setCloned(!!position?.shareLinkId)
    },[position])

    return (
        <div>
            {(delAddressLoading || delPhoneLoading ||  delDigitalLoading) && <SpinnerOverlay />}

            { !isFrenchTranslator && <div className="py-3">
                <ButtonDropdown isOpen={dropdownOpen} toggle={toggleDropdownOpen} style={{ marginRight: '15px' }} disabled={cloned}>
                    <DropdownToggle caret color="primary" disabled={cloned}>
                        <AddIcon/> Add
                    </DropdownToggle>
                    <DropdownMenu style={{ overflowY: 'auto' }}>
                        <DropdownItem onClick={toggleAddressAdd}>{getPositionLbl()} Address</DropdownItem>
                        <DropdownItem onClick={toggleContactPhoneAdd}>{getPositionLbl()} Phone Number</DropdownItem>
                        <DropdownItem onClick={toggleDigitalAddressAdd}>{getPositionLbl()} Email Address</DropdownItem>
                    </DropdownMenu>
                </ButtonDropdown>
                <Button color="danger" style={{ marginRight: '15px' }} onClick={removeContacts} disabled={isNoneSelected()||cloned}>
                    <DeleteIcon/> Remove
                </Button>
            </div>
            }

            <CollapsablePanel headTitle={`${getPositionLbl()} Addresses Information`} initCollapse={false} helpUrl="">
                <PositionAddress position={position} checkGroup={addressCheckGroup}/>
            </CollapsablePanel>

            <Spacer/>
            <CollapsablePanel headTitle={`${getPositionLbl()} Phone Numbers`} initCollapse={false} helpUrl="">
                <PositionPhone position={position} checkGroup={phoneCheckGroup}/>
            </CollapsablePanel>

            <Spacer/>
            <CollapsablePanel headTitle={`${getPositionLbl()} Email Addresses`} initCollapse={false} helpUrl="">
                <PositionEmail position={position} checkGroup={digitalAddressCheckGroup}/>
            </CollapsablePanel>

            <AddressCreate positionId={position?.positionId} isRole={position?.isRole} isOpen={addressAddOpen} toggle={toggleAddressAdd}/>
            <PhoneCreate positionId={position?.positionId} isRole={position?.isRole} isOpen={contactPhoneAddOpen} toggle={toggleContactPhoneAdd}/>
            <EmailCreate positionId={position?.positionId} isRole={position?.isRole} isOpen={digitalAddressAddOpen} toggle={toggleDigitalAddressAdd}/>
        </div>
    )
}

export default PositionContactInfo
